import React from "react";
import { ArrowUpRight, Heart } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import BannerImage from "@/components/BannerImage";

const IMPACT_BANNER =
  "https://customer-assets.emergentagent.com/job_lideranca-impacto/artifacts/3jh0gyze_WhatsApp%20Image%202026-04-21%20at%2022.33.38.jpeg";

export default function Instituto() {
  const r = useReveal();
  return (
    <section
      id="instituto"
      data-testid="instituto-section"
      className="relative section-pad bg-[#0B1E36] text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-25">
        <img
          src="https://images.pexels.com/photos/27400628/pexels-photo-27400628.jpeg"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          aria-hidden
        />
      </div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, rgba(11,30,54,0.92) 0%, rgba(11,30,54,0.75) 60%, rgba(11,30,54,0.55) 100%)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-noise" aria-hidden />

      <div ref={r} className="reveal relative max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center mb-6">
              <span className="accent-rule bg-[#C5A059]" />
              <span className="text-[0.72rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                Instituto Garoto da Arena
              </span>
            </div>
            <h2 className="font-serif-display text-[2.5rem] sm:text-[2.9rem] md:text-5xl lg:text-[4.25rem] leading-[1.05] tracking-[-0.028em] font-medium">
              Onde a fé se faz
              <span
                className="italic text-[#EBC77A]"
                style={{
                  textShadow:
                    "0 1px 0 rgba(11,30,54,0.45), 0 0 22px rgba(197,160,89,0.35)",
                }}
              >
                {" "}
                ação concreta
              </span>{" "}
              e transforma vidas.
            </h2>
            <p className="mt-7 text-white/75 text-[1.05rem] md:text-[1.18rem] leading-[1.78] font-light max-w-2xl">
              O Instituto Garoto da Arena (GA+) transforma vidas por meio do
              esporte, das ações sociais e das terapias integradas, promovendo
              inclusão e desenvolvimento com base em valores cristãos e
              familiares.
              <br />
              <br />
              Atuando em Jaboatão dos Guararapes, impactamos diariamente
              crianças, jovens e famílias, oferecendo cuidado espiritual,
              atendimento especializado em neurodiversidade (ENAGA),
              atividades esportivas e projetos sociais contínuos.
            </p>
            <p
              data-testid="instituto-emotional-line"
              className="mt-7 font-serif-display italic text-white/95 text-[1.2rem] md:text-[1.35rem] leading-[1.55] tracking-[-0.01em] max-w-2xl border-l-2 border-[#C5A059]/60 pl-6"
            >
              Cada ação vai além de um projeto. É uma vida transformada, uma
              família fortalecida e um propósito sendo cumprido.
            </p>
            <div
              data-testid="instituto-stats"
              className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl"
            >
              <div>
                <div className="font-serif-display text-3xl md:text-[2.5rem] text-[#C5A059] leading-none tabular-nums">
                  1.500
                </div>
                <div className="mt-2 text-[0.7rem] uppercase tracking-[0.22em] text-white/60">
                  Famílias atendidas por ano
                </div>
              </div>
              <div>
                <div className="font-serif-display text-3xl md:text-[2.5rem] text-[#C5A059] leading-none tabular-nums">
                  +11 mil
                </div>
                <div className="mt-2 text-[0.7rem] uppercase tracking-[0.22em] text-white/60">
                  Atendimentos de saúde
                </div>
              </div>
              <div>
                <div className="font-serif-display text-3xl md:text-[2.5rem] text-[#C5A059] leading-none tabular-nums">
                  5
                </div>
                <div className="mt-2 text-[0.7rem] uppercase tracking-[0.22em] text-white/60">
                  Anos cuidando de famílias com TEA
                </div>
              </div>
              <div>
                <div className="font-serif-display text-3xl md:text-[2.5rem] text-[#C5A059] leading-none tabular-nums">
                  6
                </div>
                <div className="mt-2 text-[0.7rem] uppercase tracking-[0.22em] text-white/60">
                  Frentes de atuação
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="space-y-6">
              {[
                {
                  n: "Base Espiritual",
                  t: "Discipulado, oração e acolhimento como fundamentos do cuidado integral.",
                },
                {
                  n: "Atendimento Especializado (ENAGA)",
                  t: "Atendimento multiprofissional voltado à neurodiversidade, com foco em crianças com TEA.",
                },
                {
                  n: "Formação Esportiva e Cultural",
                  t: "Modalidades esportivas como ferramentas de formação de caráter e disciplina.",
                },
                {
                  n: "Impacto Social",
                  t: "Ações contínuas como reforço escolar, apoio a famílias e desenvolvimento comunitário.",
                },
              ].map((item) => (
                <div
                  key={item.n}
                  className="flex gap-6 border-l border-[#C5A059]/40 pl-6 py-2"
                >
                  <div>
                    <h3 className="font-serif-display text-2xl text-[#C5A059]">
                      {item.n}
                    </h3>
                    <p className="mt-2 text-white/75 leading-[1.75] font-light">
                      {item.t}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-white/15 pt-8">
          <p className="text-white/70 max-w-xl leading-relaxed">
            <Heart size={16} strokeWidth={1.5} className="inline-block mr-2 text-[#C5A059]" />
            A aquisição dos livros contribui diretamente com o Instituto.
          </p>
          <a
            href="https://institutogaa.com.br/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instituto-cta"
            className="btn-ghost-light"
          >
            Quero fazer parte dessa missão
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>

        <div className="mt-12" data-testid="instituto-banner-wrap">
          <BannerImage
            src={IMPACT_BANNER}
            alt="Missão MAIS: Mais que alcança, Amor que transforma, Inclusão que acolhe, Serviço que cura. Instituto Garotos da Arena"
            testId="instituto-banner"
          />
        </div>
      </div>
    </section>
  );
}
